import React from "react";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { Spinner, ImgIcon } from "../../../assets";
import { deleteShopProduct, updateShopProduct } from "../../../actions/shop";
import { payloadToFormData } from "../../../helpers";
import { useDeviceSize, MOBILE } from "../../../assets/hooks/useDeviceSize";
import { remove, cross, reset } from "../../../assets/icons";
import BasicTab from "./BasicTab";
import VariantsTab from "./VariantsTab";
import OptionsTab from "./OptionsTab";

// Tab constants for navigating.
const BASIC = "BASIC",
	VARIANTS = "VARIANTS",
	OPTIONS = "OPTIONS";

export default function ProductEditor({ product, variants, onSave, onExit, categories = [] }) {

	const dispatch = useDispatch();
	const [changes, setChanges] = useState({});
	const { device } = useDeviceSize();
	const [tab, setTab] = useState(BASIC);

	const values = {
		images: [],
		...product,
		...changes
	}

	const canSubmit = Boolean(
		values.name &&
			values.price &&
			values.category &&
			(values.options ? !values.options.find(o => !o.addOns.length) : true)
	);

	const isEditing = Boolean(product.id);

	useEffect(() => {
		setChanges({});
	}, [product]);

	function onChange(newChanges) {
		setChanges({
			...changes,
			...newChanges
		});
	}

	const actions = [

		// Clearing changes.
		Boolean(Object.keys(changes).length) && {
			name: "Clear changes",
			handler: () => setChanges({}),
			icon: reset
		},

		// Deleting a product.
		isEditing && {
			name: "Delete product",
			handler: () => {
				if (!window.confirm(`Are you sure you want to remove ${product.name}? This cannot be undone. If you just want to hide the product, click the eye in the product list.`)) return;
				dispatch(deleteShopProduct(product.id, changes));
			},
			icon: remove
		},

		// Closing the editor.
		onExit && {
			name: "Close editor",
			handler: onExit,
			icon: cross
		},

	].filter(a => a);

	return <div
		className={[
			"productEditor",
			`productEditor-${values.id ? "editing" : "creating"}`,
			Boolean(values.id) && `product-${values.available ? "available" : "disabled"}`,
		].filter(c => c).join(" ")}
		style={device !== MOBILE && isEditing ? {
			border: "4px solid #ce003b"
		} : {}}
	>
		<div className="inner">
			{/* heading shows product name and standard controls */}
			<div className="product-header flexy padded" style={{
				paddingRight: (actions.length * 30) + ((actions.length - 1) * 5) + 15
			}}>
				<h4 title={product.id || null} style={{ flex: 2 }}>
					{values.name || "..."}
				</h4>
				<div className="controls v-middle text-right" style={{ fontSize: 0 }}>
					{actions.map(({ icon, handler, name }) => (
						<ImgIcon
							key={name}
							title={name}
							src={icon}
							onClick={handler}
							size={30}
							iconSize={"65%"}
							bg={"#ffffff"}
						/>
					))}
				</div>
			</div>
			{/* Navigation for product tabs */}
			{isEditing && <div className="editor-nav featureFont flexy">
				{[BASIC, VARIANTS, OPTIONS].map(tabName => {
					const isCurrent = tabName === tab;
					return <div
						className={`padded tab-link ${isCurrent ? "tab-link-current" : ""}`}
						key={tabName}
						onClick={!isCurrent ? e => setTab(tabName) : null}
					>{tabName}</div>
				})}
			</div>}
			{/* The actual tabbed content */}
			<div className="tab-body">
				{tab === BASIC && <BasicTab
					values={values}
					product={product}
					onChange={onChange}
					categories={categories}
				/>}
				{tab === VARIANTS && <VariantsTab
					values={values}
					product={product}
					onChange={onChange}
					variants={variants}
				/>}
				{tab === OPTIONS && <OptionsTab
					values={values}
					product={product}
					onChange={onChange}
				/>}
			</div>


			{/* Buttons to save/create the product */}
			{isEditing && Boolean(Object.keys(changes).length) &&
				<div className="padded">
					<button
						style={{ width: "100%" }}
						disabled={!canSubmit}
						// style={{ backgroundColor: "#CE003B" }}
						onClick={e => {
							if (!canSubmit) return;
							const formData = payloadToFormData(changes);
							dispatch(updateShopProduct(product.id, formData))
						}}
					>
						{product.loading ?
							<Spinner />
							:
							"Save"
						}
					</button>
				</div>
			}
			{!isEditing &&
				<div className="padded">
					<button
						style={{ width: "100%" }}
						disabled={!canSubmit}
						onClick={e => {
							if (!canSubmit) return;
							if (onSave) onSave({ ...changes });
						}}
					>
						{product.loading ?
							<Spinner />
							:
							"Create"
						}
					</button>
				</div>
			}
		</div>
	</div >
}